import { LogLevel } from '../logger/log-levels.enum';
import { EventMessage, LogMessage } from '../models';
import { TransporterConfig } from './transporter-config.model';
import { TransportTemplate } from './transporter-template.model';
import { Transporter } from './transporter.model';

/**
 * Base Transporter implementation
 */
export abstract class AbstractTransporterDecorator<T extends EventMessage = LogMessage> implements Transporter<T> {
  protected debugActions: { [key: string]: LogLevel } = {};

  /**
   * local null checker
   * @param x
   * @returns
   */
  protected _notNull = x => x != null;

  constructor(
    public template: TransportTemplate<T>,
    public config?: TransporterConfig
  ) {
    /*
     * convert debug actions as a dictionary of events with their respective configured log level
     * if no level given then it's global level is used
     */
    if (this._notNull(config)) {
      this.debugActions = this.config.debugActions || {};
    }
  }

  /**
   * If payload evaluates as should be logged then a macro-task is scheduled
   * @param logLevel
   * @param payload
   */
  write(payload: T, level: LogLevel): void {
    if (
      this._notNull(payload) &&
      (this.isEventLevelLessThanLogLevel(level) || this.isIncludedInDebugActions(level, payload))
    ) {
      // final write
      return this.doWrite(payload, level);
    }
  }

  /**
   * Abstract writing process, to be implemented by any concrete class (http, email, console, slack, teams, etc... concrete classes)
   * @param payload
   * @param level
   */
  abstract doWrite(payload: T, level?: LogLevel): void;

  /**
   * A log-able payload is it which it's log level is less or equal to configured log level
   * @param logLevel
   * @param payload
   */
  private isEventLevelLessThanLogLevel(logLevel: LogLevel): boolean {
    return this.config.level >= logLevel;
  }

  /**
   * A log-able event is it which it's included in a set of configured debug actions and logLevel
   * is less or equal to it's event log level (if provided) or configured log level
   * @param logLevel
   * @param payload
   */
  protected isIncludedInDebugActions(logLevel: LogLevel, payload: T): boolean {
    return (
      // OR if payload has eventName
      payload.eventName != null && // and event log level is less than event log level (inherited from global if not provided)
      this.debugActions[payload.eventName] >= logLevel
    );
  }
}
